import React, { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, Loader2, Umbrella, Eye, EyeOff, X } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';

const EMPTY = { name_es: '', name_en: '', name_de: '', zone: '', latitude: 28.3, longitude: -16.5, swim: true, surf: false, wave_factor: 1, level: '', break_type: '', is_active: true, bot_notes: '' };

const Field = ({ label, children }) => (
  <div className="space-y-1"><Label className="text-xs text-gray-600">{label}</Label>{children}</div>
);

const SpotForm = ({ initial, onSaved, onCancel }) => {
  const [form, setForm] = useState({ ...EMPTY, ...initial, level: initial?.level || '', break_type: initial?.break_type || '' });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, latitude: Number(form.latitude), longitude: Number(form.longitude), wave_factor: Number(form.wave_factor) || 1, level: form.level || null, break_type: form.break_type || null };
    ['id', 'created_at', 'updated_at', 'order'].forEach((k) => delete payload[k]);
    if (!payload.level) delete payload.level;
    if (!payload.break_type) delete payload.break_type;
    try {
      if (initial?.id) await api.adminUpdateSpot(initial.id, payload);
      else await api.adminCreateSpot(payload);
      toast.success('Playa guardada');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail?.missing ? `Faltan: ${err.response.data.detail.missing.join(', ')}` : 'No se pudo guardar');
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="bg-white border border-gray-200 p-5 space-y-4" data-testid="spot-form">
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-[#1D1D1F]">{initial?.id ? 'Editar playa / spot' : 'Nueva playa / spot'}</h3>
        <button type="button" onClick={onCancel} className="p-1 text-gray-400 hover:text-gray-700" data-testid="spot-form-close"><X size={18} /></button>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <Field label="Nombre ES"><Input value={form.name_es} onChange={(e) => set('name_es', e.target.value)} required data-testid="spot-name-es" /></Field>
        <Field label="Nombre EN"><Input value={form.name_en} onChange={(e) => set('name_en', e.target.value)} data-testid="spot-name-en" /></Field>
        <Field label="Nombre DE"><Input value={form.name_de} onChange={(e) => set('name_de', e.target.value)} data-testid="spot-name-de" /></Field>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <Field label="Zona / municipio"><Input value={form.zone} onChange={(e) => set('zone', e.target.value)} data-testid="spot-zone" /></Field>
        <Field label="Latitud"><Input type="number" step="0.0001" value={form.latitude} onChange={(e) => set('latitude', e.target.value)} required data-testid="spot-lat" /></Field>
        <Field label="Longitud"><Input type="number" step="0.0001" value={form.longitude} onChange={(e) => set('longitude', e.target.value)} required data-testid="spot-lon" /></Field>
      </div>
      <div className="flex flex-wrap items-center gap-6">
        <label className="flex items-center gap-2 text-sm"><Switch checked={!!form.swim} onCheckedChange={(v) => set('swim', v)} data-testid="spot-swim" /> Sale en Playas</label>
        <label className="flex items-center gap-2 text-sm"><Switch checked={!!form.surf} onCheckedChange={(v) => set('surf', v)} data-testid="spot-surf" /> Sale en Surf</label>
        <label className="flex items-center gap-2 text-sm"><Switch checked={!!form.is_active} onCheckedChange={(v) => set('is_active', v)} data-testid="spot-active" /> Activa</label>
      </div>
      {form.surf && (
        <div className="grid grid-cols-2 gap-2">
          <Field label="Nivel de surf">
            <select value={form.level} onChange={(e) => set('level', e.target.value)} className="w-full h-10 border border-gray-200 px-2 text-sm bg-white" data-testid="spot-level">
              <option value="">—</option><option value="beginner">Iniciación</option><option value="intermediate">Intermedio</option><option value="advanced">Avanzado</option>
            </select>
          </Field>
          <Field label="Tipo de ola">
            <select value={form.break_type} onChange={(e) => set('break_type', e.target.value)} className="w-full h-10 border border-gray-200 px-2 text-sm bg-white" data-testid="spot-break">
              <option value="">—</option><option value="beach">Beach break</option><option value="reef">Reef</option><option value="point">Point break</option>
            </select>
          </Field>
        </div>
      )}
      <Field label={`Abrigo de la playa: ×${Number(form.wave_factor ?? 1).toFixed(1)} (1 = mar abierto · 0.4 = bahía con espigón)`}>
        <Slider value={[Number(form.wave_factor ?? 1)]} min={0.1} max={1.5} step={0.1} onValueChange={([v]) => set('wave_factor', v)} data-testid="spot-wave-factor" />
      </Field>
      <Field label="Notas internas para el bot">
        <textarea value={form.bot_notes || ''} onChange={(e) => set('bot_notes', e.target.value)} rows={2} className="w-full border border-amber-200 bg-amber-50/40 p-2 text-sm" data-testid="spot-bot-notes" />
      </Field>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel}>Cancelar</Button>
        <Button type="submit" disabled={saving} className="bg-[#38B2AC] hover:bg-[#2C8F8A]" data-testid="spot-save">{saving && <Loader2 size={16} className="animate-spin mr-2" />}Guardar</Button>
      </div>
    </form>
  );
};

const AdminSpots = () => {
  const [spots, setSpots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState('all');

  const load = () => api.adminGetSpots().then((r) => setSpots(r.data)).catch(() => toast.error('No se pudieron cargar las playas')).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const toggleActive = async (s) => {
    try { await api.adminUpdateSpot(s.id, { is_active: !s.is_active }); load(); } catch { toast.error('No se pudo actualizar'); }
  };
  const remove = async (s) => {
    if (!window.confirm(`¿Eliminar «${s.name_es}»?`)) return;
    try { await api.adminDeleteSpot(s.id); toast.success('Eliminada'); load(); } catch { toast.error('No se pudo eliminar'); }
  };

  const shown = spots.filter((s) => filter === 'all' || (filter === 'swim' ? s.swim : s.surf));
  if (loading) return <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-[#38B2AC]" /></div>;

  return (
    <div className="space-y-4 pt-8" data-testid="admin-spots">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-bold text-lg text-[#1D1D1F] flex items-center gap-2"><Umbrella size={18} className="text-[#38B2AC]" /> Playas y spots de surf</h2>
          <p className="text-sm text-gray-500">Listas de las pestañas «Playas» y «Surf». Desactiva las que no quieras mostrar.</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="h-9 border border-gray-200 px-2 text-sm bg-white" data-testid="spot-filter">
            <option value="all">Todas ({spots.length})</option><option value="swim">Playas</option><option value="surf">Surf</option>
          </select>
          {!editing && <Button onClick={() => setEditing({})} className="bg-[#38B2AC] hover:bg-[#2C8F8A]" data-testid="spot-add-btn"><Plus size={16} className="mr-1" /> Nueva</Button>}
        </div>
      </div>

      {editing && <SpotForm initial={editing} onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />}

      <div className="bg-white border border-gray-200 divide-y divide-gray-100">
        {shown.map((s) => (
          <div key={s.id} className={`flex items-center gap-3 px-4 py-2.5 ${s.is_active ? '' : 'opacity-50'}`} data-testid={`spot-row-${s.id}`}>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-[#1D1D1F] truncate">{s.name_es} <span className="font-normal text-gray-400 text-sm">· {s.zone}</span></p>
              <p className="text-xs text-gray-500 truncate">{s.swim ? 'Playas' : ''}{s.swim && s.surf ? ' · ' : ''}{s.surf ? `Surf${s.level ? ` (${s.level})` : ''}` : ''} · abrigo ×{(s.wave_factor ?? 1).toFixed(1)}</p>
            </div>
            <button onClick={() => toggleActive(s)} className="p-2 text-gray-500 hover:text-[#38B2AC]" title={s.is_active ? 'Desactivar' : 'Activar'} data-testid={`spot-toggle-${s.id}`}>{s.is_active ? <Eye size={16} /> : <EyeOff size={16} />}</button>
            <button onClick={() => setEditing(s)} className="p-2 text-gray-500 hover:text-[#38B2AC]" data-testid={`spot-edit-${s.id}`}><Pencil size={16} /></button>
            <button onClick={() => remove(s)} className="p-2 text-gray-500 hover:text-red-600" data-testid={`spot-delete-${s.id}`}><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminSpots;
